import axios from "axios";

import { UrlApi } from "../../../services/apirest";
const API = `${UrlApi}/audit`;

// ── Helper para requests con FormData (archivos) ──────────────
const multipart = { headers: { "Content-Type": "multipart/form-data" } };

// ── Helper: arma FormData desde un objeto + array de files ────
function buildFormData(data, files = [], fieldName = "adjuntos") {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => {
        if (v !== undefined && v !== null) fd.append(k, v);
    });
    if (files?.length) {
        files.forEach((f) => fd.append(fieldName, f));
    }
    return fd;
}

// ══════════════════════════════════════════════════════════════
//  MAESTROS
// ══════════════════════════════════════════════════════════════

/** Lista de estados activos ordenados */
export const getEstados = () =>
    axios.get(`${API}/estados`).then((r) => r.data);

/** Sucursales activas para el select */
export const getSucursales = () =>
    axios.get(`${API}/sucursales`).then((r) => r.data);

/** Usuarios con flag controlador=1 */
export const getControladores = () =>
    axios.get(`${API}/controladores`).then((r) => r.data);

/** Categorías activas para el select */
export const getCategorias = () =>
    axios.get(`${API}/categorias`).then((r) => r.data);

/** Advertencias activas para el select */
export const getAdvertencias = () =>
    axios.get(`${API}/advertencias`).then((r) => r.data);

// ══════════════════════════════════════════════════════════════
//  ÍTEMS
// ══════════════════════════════════════════════════════════════

/**
 * Lista paginada de ítems con filtros opcionales.
 * @param {Object} params - { username, status_id, sucursal_id,
 *                            prioridad, desde, hasta, page, limit }
 */
export const getItems = (params) =>
    axios
        .get(`${API}/items`, { params })
        .then((r) => r.data);

/**
 * Detalle completo de un ítem (tracking + adjuntos).
 * @param {number} id
 * @param {string} username
 */
export const getItem = (id, username) =>
    axios
        .get(`${API}/items/${id}`, { params: { username } })
        .then((r) => r.data);

/**
 * Crear nuevo ítem con adjuntos opcionales.
 * @param {Object} data  - campos del ítem
 * @param {File[]} files - adjuntos
 */
export const crearItem = (data, files = []) =>
    axios
        .post(`${API}/items`, buildFormData(data, files), multipart)
        .then((r) => r.data);

/**
 * Editar campos del ítem (sin cambio de estado ni reasignación).
 * @param {number} id
 * @param {Object} data - campos + username
 */
export const editarItem = (id, data) =>
    axios.put(`${API}/items/${id}`, data).then((r) => r.data);

// ══════════════════════════════════════════════════════════════
//  TRACKING
// ══════════════════════════════════════════════════════════════

/**
 * Registrar avance / cambio de estado con adjuntos opcionales.
 * @param {number} itemId
 * @param {Object} data  - { username, status_nuevo, tipo_accion, comentario }
 * @param {File[]} files - adjuntos
 */
export const agregarTracking = (itemId, data, files = []) =>
    axios
        .post(
            `${API}/items/${itemId}/tracking`,
            buildFormData(data, files),
            multipart
        )
        .then((r) => r.data);

// ══════════════════════════════════════════════════════════════
//  REASIGNACIÓN
// ══════════════════════════════════════════════════════════════

/**
 * Reasignar controlador de un ítem.
 * @param {number} itemId
 * @param {Object} data - { username, nuevo_controlador_username, motivo }
 */
export const reasignarItem = (itemId, data) =>
    axios
        .post(`${API}/items/${itemId}/reasignar`, data)
        .then((r) => r.data);

// ══════════════════════════════════════════════════════════════
//  ADJUNTOS
// ══════════════════════════════════════════════════════════════

/** Helper interno: headers con token */
const authHeaders = () => ({
    Authorization: localStorage.getItem("token") || "",
});

/**
 * Descarga un adjunto de forma segura con token en header.
 * Crea un blob URL temporal y dispara la descarga.
 */
export const descargarAdjunto = async (adjuntoId, nombreOriginal) => {
    const response = await fetch(`${API}/adjuntos/${adjuntoId}/download`, {
        headers: authHeaders(),
    });
    if (!response.ok) throw new Error("Error al descargar el archivo");
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = nombreOriginal || "archivo";
    a.click();
    URL.revokeObjectURL(url);
};

/**
 * Abre un adjunto en nueva pestaña para previsualizar (imagen o PDF).
 * El token va en el header, nunca en la URL.
 */
export const previsualizarAdjunto = async (adjuntoId) => {
    const response = await fetch(`${API}/adjuntos/${adjuntoId}/preview`, {
        headers: authHeaders(),
    });
    if (!response.ok) throw new Error("Error al cargar el archivo");
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
};

/** Tipos MIME que se pueden previsualizar en el browser */
export const esPrevisualizble = (mimeType) => {
    if (!mimeType) return false;
    return mimeType.startsWith("image/") || mimeType === "application/pdf";
};

// ── Log global ───────────────────────────────────────────────
export const getLog = (params) =>
    axios.get(`${API}/log`, { params }).then(r => r.data);

// ── Estadísticas ──────────────────────────────────────────────
// Todas aceptan un objeto params: { username, periodo?, anio?, controlador_username? }
export const getStatsResumen = (params) =>
    axios.get(`${API}/stats/resumen`, { params }).then(r => r.data);

export const getStatsEvolucion = (params) =>
    axios.get(`${API}/stats/evolucion`, { params }).then(r => r.data);

export const getStatsControladores = (params) =>
    axios.get(`${API}/stats/controladores`, { params }).then(r => r.data);

export const getStatsVencidos = (params) =>
    axios.get(`${API}/stats/vencidos`, { params }).then(r => r.data);